import cx from "classnames";
import get from "lodash/get";
import startsWith from "lodash/startsWith";
import React from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { useStableContext } from "../rill/RillReact/share/base";
import checkMenuExtended from "../utils/checkMenuExtended";
import getAvatarUrl from "../utils/get-avatar-url";
import { openMsbAgent, useMsbAgentSeen } from "../utils/msb-agent";
import {
  calculateFreeTrialDaysLeft,
  getOrganizationCoach,
  isCoachFreeTrial,
  isPullActiveCoach,
  isPushOrPerformClient,
  isTestAthlete,
} from "../utils/user-helpers";
import { getRoleNameById } from "../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { AthleteLinks } from "./athlete-links";
import DownloadCoachMobileAppButtonHeader from "./DownloadCoachMobileAppButtonHeader";
import { HeaderProfile } from "./header-elements";
import { AthleteSelect } from "./inputs/athlete-select-new";
import NewsDrawer from "./news-drawer/NewsDrawer";
import NotificationsDropdown from "./notificationsDropdown";
import RecordButtonHeader from "./RecordButtonHeader";
import SvgIcon from "./SvgIcon";

const coachingRoutes = [
  "/coaching-portal/coaching-dashboard",
  "/coaching-portal/athletes",
  "/coaching-portal/tasks",
];

function Header({
  coachingPortalEnabled,
  athletePortalEnabled = false,
  mobile = false,
  handleAthleteSearch = () => {},
  query = {},
}) {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const { locals: stableLocals } = useStableContext();
  const {
    activeUser,
    organizationOwner,
    activeUserToRequest,
    athlete = {},
    pathname,
    organizationRoles,
    userByEmail,
    unreadNewsCount = 0,
  } = stableLocals;
  const hasSeenMsbAgent = useMsbAgentSeen(activeUser);
  // Merge query (from props) back into locals for child components
  // that receive the full locals object (e.g. RecordButtonHeader, HeaderProfile)
  const locals = React.useMemo(
    () => ({ ...stableLocals, query }),
    [stableLocals, query]
  );
  const isNewsDrawerOpen = query?.modal?.type === "whats-new";
  const { _id: userId, referralProgramInfo = {} } = activeUser;
  const { link: referralLink = "", showReferralButton = true } =
    referralProgramInfo;
  const isAdmin = activeUser.admin || activeUser.hasRole("admin");
  const isCoach = get(activeUser, "permissions", []).includes("coach");
  const isInvitedAthlete = get(activeUser, "coach", null);
  const isSubscriber =
    (!activeUser.organizationId && !organizationOwner) || !isCoach
      ? isPushOrPerformClient(activeUser)
      : isPushOrPerformClient(organizationOwner);
  const daysLeft = calculateFreeTrialDaysLeft(activeUser);

  const isCoachingPage = coachingRoutes.some((route) =>
    pathname.startsWith(route)
  );
  const isMenuExtended = checkMenuExtended(pathname);

  const showTestApp = React.useMemo(() => {
    const {
      athletes = [],
      pendingAthleteInvitations = [],
      email,
    } = activeUserToRequest;
    if (!athletes.length && !pendingAthleteInvitations.length) return false;
    const isAthleteList = pathname.startsWith("/coaching-portal/athletes");
    const testAthlete =
      athletes.find((athlete) => isTestAthlete(athlete.email, email)) ||
      pendingAthleteInvitations.find((athleteEmail) =>
        isTestAthlete(athleteEmail, email)
      );
    return isAthleteList && !testAthlete;
  }, [activeUserToRequest, pathname]);

  const showPushTestApp = React.useMemo(() => {
    const isStore = pathname === "/coaching-portal/store";
    const isPushInvited = userByEmail && userByEmail !== null;
    const appStoreListingEnable = activeUser.appStoreListingEnable;

    return isStore && appStoreListingEnable && !isPushInvited;
  }, [activeUser, pathname, userByEmail]);

  const isUserInCoachingPortal = Boolean(pathname?.includes("coaching-portal"));
  const userHasCustomReferralLink = referralLink.length > 0;
  const isActivePull = isPullActiveCoach(activeUser);
  const isCoachTrial = isCoachFreeTrial(activeUser);
  const roleName = activeUser.organizationRoleId
    ? getRoleNameById(organizationRoles, activeUser.organizationRoleId)
    : false;
  const isOwner = roleName === "Owner";
  /**
   * Function to close forever the container who shows the referral link information
   * @param {*} e
   */
  const handleNotShowReferralContainer = (e) => {
    e.preventDefault();
    submit("/actions/user/update-referral-program", {
      method: "POST",
      body: {
        purpose: "hide-link",
        userId,
        referralProgramInfo: {
          link: referralLink,
          showReferralButton: false,
        },
      },
    });
  };
  const openNewsDrawer = () => {
    submit("/actions/refresh", {
      method: "GET",
      body: { "modal[type]": "whats-new" },
    });
  };
  const showActivityFeed = () => {
    let body = { "modal[type]": "activity-feed" };
    if (isOwner) body = { ...body, isOwner: true };
    submit("/actions/refresh", {
      method: "GET",
      body,
    });
  };
  return (
    <header role="main">
      {isNewsDrawerOpen && <NewsDrawer />}
      {!isMenuExtended && !athletePortalEnabled && (
        <div>
          <img alt={t("header.msbLogoAlt")} src="/img/svg/MSB-ico.svg" />
        </div>
      )}
      {isMenuExtended && !athletePortalEnabled && isCoachingPage ? (
        mobile ? ( //not-reviewed
          <div role="search-icon-wrapper" onClick={handleAthleteSearch}>
            <img
              className="icon"
              src="/img/svg/search-ico.svg"
              alt={t("common:search")}
            />
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <AthleteSelect
              locals={locals}
              refreshWithName={true}
              handleAthleteChange={(athleteId) => {
                const { usersOrganization } = locals;
                const { organizationActiveUsers } = usersOrganization || [];

                const { isFromOtherOrganizationMember, organizationCoachId } =
                  getOrganizationCoach(
                    locals.activeUser,
                    athleteId,
                    organizationActiveUsers
                  );
                let body = { athlete: athleteId };
                if (isFromOtherOrganizationMember)
                  body = { ...body, organizationCoach: organizationCoachId };

                submit(`/athlete-portal/profile`, {
                  method: "GET",
                  body,
                });
              }}
            />
            {isCoach && (
              <DownloadCoachMobileAppButtonHeader
                activeUser={activeUser}
                isActivePull={isActivePull}
              />
            )}
          </div>
        )
      ) : athletePortalEnabled ? (
        athletePortalEnabled && (
          <div className="flex flex-row gap-2 items-center">
            <div className="athlete-select dropdown-athlete">
              <div
                className="avatar"
                style={{
                  backgroundImage: `url(${getAvatarUrl(locals.athlete)})`,
                }}
              />
              <AthleteSelect
                locals={locals}
                {...(pathname?.startsWith("/athlete-portal/profile") &&
                  query?.section === "periodization" && {
                    athleteSwitch: true,
                  })}
              />
            </div>

            <RecordButtonHeader
              locals={locals}
              BUTTON_ID={"loom-record-sdk-button-header"}
            />
          </div>
        )
      ) : (
        <div className="flex items-center gap-4">
          {pathname.startsWith("/coaching-portal/community-chat") ? (
            <h2>{t("header.communityChat")}</h2>
          ) : pathname.startsWith("/coaching-portal/chat") ? (
            <h2>{t("header.chats")}</h2>
          ) : (
            <></>
          )}
          {isCoach && (
            <DownloadCoachMobileAppButtonHeader
              activeUser={activeUser}
              isActivePull={isActivePull}
            />
          )}
        </div>
      )}

      <hgroup>
        {isCoach && showTestApp && (
          <a
            role="button"
            data-button-type="outline"
            href="/coaching-portal/athletes/?modal[type]=test-account"
          >
            <span>{t("header.testYourApp")}</span>
          </a>
        )}
        {isCoach &&
          activeUserToRequest.coachingFreeTrial &&
          !athletePortalEnabled &&
          daysLeft > 0 && (
            <div className="free-trial-header-div">
              <a
                href="/coach-billing"
                role="button"
                data-button-type="secondary"
              >
                {t("header.freeTrialDaysLeft", { daysLeft })}
                <span className="clipped font-semibold">
                  &nbsp;{t("common:upgradeNow")}
                </span>
              </a>
            </div>
          )}
        {isUserInCoachingPortal &&
          !userHasCustomReferralLink &&
          isActivePull &&
          !isCoachTrial &&
          showReferralButton && (
            <div className="free-trial-header-div mobile-hidden">
              <a
                href="https://mystrengthbook.firstpromoter.com/"
                role="button"
                data-button-type="secondary"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="referral-div">
                  <span className="referral-text">
                    {t("header.earnCashWithOur")}
                  </span>
                  <span className="clipped font-semibold h-5">
                    &nbsp;{t("header.referralProgram")}
                  </span>
                </div>
                <button
                  className="referral-icon"
                  onClick={(e) => {
                    handleNotShowReferralContainer(e);
                  }}
                  type="button"
                >
                  <img
                    src="/img/new-icons/cross-light.svg"
                    className="filter-grey"
                    alt="Close"
                  />
                </button>
              </a>
            </div>
          )}
        {athletePortalEnabled && (
          <AthleteLinks athlete={athlete} pathName={pathname} query={query} />
        )}
        {isCoach && !isMenuExtended && !athletePortalEnabled && (
          <a href="/coaching-portal" role="button" data-button-type="outline">
            <span>{t("header.coaching")}</span>
          </a>
        )}
        <div className="nav-icons">
          {!isCoach && (
            <a
              href="/dashboard/support/contact"
              role="button"
              data-button-type="outline"
            >
              <SvgIcon src="/img/svg/need-help-icon.svg" />
            </a>
          )}

          {/* Test your app */}

          {isCoach && showPushTestApp && (
            <a
              href={`/actions/refresh?modal[type]=test-account`}
              role="button"
              data-button-type="outline"
            >
              {t("header.testMyPushApp")}
            </a>
          )}

          <a
            onClick={openNewsDrawer}
            className="news-icon-btn"
            role="button"
            tabIndex="0"
          >
            <SvgIcon src="/img/new-icons/megaphone.svg" />
            {unreadNewsCount > 0 && <div className="unread-nots-badge" />}
          </a>
          {isCoach && !mobile && (
            <button
              type="button"
              className="msb-agent-pill mobile-hidden"
              data-button-type="outline"
              onClick={(event) => openMsbAgent(event.currentTarget, activeUser)}
            >
              <SvgIcon src="/img/new-icons/magic-star.svg" noFilterOnLight />
              <span>{t("msbAgent.pill")}</span>
              {!hasSeenMsbAgent && (
                <span className="msb-agent-new-badge">
                  {t("msbAgent.newBadge")}
                </span>
              )}
            </button>
          )}
          {!isSubscriber && <NotificationsDropdown mobile={mobile} />}
          {!isSubscriber && mobile && (
            <div className="notifications-black-background" />
          )}

          <a
            href={
              coachingPortalEnabled || athletePortalEnabled
                ? "/coaching-portal/settings"
                : "/dashboard/settings"
            }
            role="button"
            data-button-type="secondary"
            data-icon
            className={`icon-element mobile-hidden ${cx({
              active: startsWith(
                pathname,
                coachingPortalEnabled
                  ? "/coaching-portal/settings"
                  : "/dashboard/settings"
              ),
            })}`}
          >
            <SvgIcon src="/img/new-icons/settings.svg" alt="" />
          </a>
          <a
            href={
              coachingPortalEnabled || athletePortalEnabled
                ? "/coaching-portal/settings/settings-menu"
                : "/dashboard/settings"
            }
            className={`icon-element only-show-mobile ${cx({
              active: startsWith(
                pathname,
                coachingPortalEnabled
                  ? "/coaching-portal/settings"
                  : "/dashboard/settings"
              ),
            })}`}
          >
            <SvgIcon src="/img/new-icons/settings.svg" alt="" />
          </a>
          {isCoach && (
            <button
              data-button-type="secondary"
              onClick={showActivityFeed}
              data-icon
              data-icon-only
              type="button"
            >
              <SvgIcon
                src="/img/new-icons/vuesax-linear-activity.svg"
                classNames="cursor-pointer"
              />
            </button>
          )}
        </div>
        <HeaderProfile
          activeUser={activeUser}
          isAdmin={isAdmin}
          isInvitedAthlete={isInvitedAthlete}
          isSubscriber={isSubscriber}
          locals={locals}
          activeUserToRequest={activeUserToRequest}
          daysLeft={daysLeft}
        />
      </hgroup>
    </header>
  );
}

export default React.memo(Header, (prevProps, nextProps) => {
  for (const key of Object.keys(nextProps)) {
    if (key === "query") {
      if (JSON.stringify(prevProps.query) !== JSON.stringify(nextProps.query))
        return false;
    } else if (prevProps[key] !== nextProps[key]) {
      return false;
    }
  }
  return Object.keys(prevProps).length === Object.keys(nextProps).length;
});
